import { Divider } from '@material-ui/core';
import Breadcrumbs from '@material-ui/core/Breadcrumbs';
import { green, grey, red } from '@material-ui/core/colors';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { darken } from '@material-ui/core/styles/colorManipulator';
import Tab from '@material-ui/core/Tab';
import Tabs from '@material-ui/core/Tabs';
import Typography from '@material-ui/core/Typography';
import moment from 'moment';
import { withSnackbar, WithSnackbarProps } from 'notistack';
import React, { useEffect, useState, useContext } from 'react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import WindsorApi from '../../api/WindsorApi';
import LinkRouter from '../../common/LinkRouter';
import LoadingButton from '../../common/LoadingButton';
import LoadingIndicator from '../../common/LoadingIndicator';
import ApprovalsPanel from './approvals/ApprovalsPanel';
import DiffBuilder from './diff/DiffBuilder';
import DiffView from './diff/DiffView';
import HistoryPanel from './history/HistoryPanel';
import ActivityPanel from './activity/ActivityPanel';
import NoResultsText from '../../common/NoResultsText';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import Button from '@material-ui/core/Button';
import ActionsPanel from './actions/ActionsPanel';
import * as qs from 'qs';
import ApprovalDetailsHelper from './ApprovalDetailsHelper';
import UserContext from '../../auth/UserContext';
import TimestampTooltip from '../../common/TimestampTooltip';
import MobxContext from '../../mobx/MobxContext';
import ExecutionConstants from '../../families/executions/ExecutionConstants';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      maxWidth: 1200,
      margin: 'auto'
    },
    tabs: {
      border: `1px solid ${theme.palette.divider}`,
      backgroundColor: darken(theme.palette.background.default, 0.03)
    },
    divider: {
      marginBottom: 16,
      marginTop: 8
    },
    approveButton: {
      color: green[500],
      marginRight: 8
    },
    rejectButton: {
      color: red[500]
    },
    infoPanel: {
      padding: 16,
      border: `1px solid ${theme.palette.type === 'light' ? grey[300] : grey[800]}`,
      marginBottom: 16,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between'
    },
    titleSection: {
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: 16
    },
    middleDot: {
      margin: '0 8px'
    },
    approvalDescriptionText: {
      marginRight: 16,
      display: 'flex'
    },
    tabContent: {
      paddingTop: 16
    },
    executionLink: {
      margin: '0 4px'
    }
  })
);

const windsorApi = new WindsorApi();
const diffBuilder = new DiffBuilder(windsorApi);

const TabContent = ({ display, children, className }) => <div style={{ display: display ? 'block' : 'none' }} className={className}>{children}</div>;

function ApprovalsDetailsPage(props: RouteComponentProps<any> & WithSnackbarProps) {
  const { match, enqueueSnackbar, history, location } = props;
  const { tab = 'activity' } = qs.parse(location.search, { ignoreQueryPrefix: true });
  const approvalId = match.params.approvalId;

  const classes = useStyles({});
  const [approval, setApproval] = useState(null);
  const [approvalNotFound, setApprovalNotFound] = useState(false);
  const [execution, setExecution] = useState(null);
  const [groups, setGroups] = useState(null);
  const [diffs, setDiffs] = useState(null);
  const [loadChanges, setLoadChanges] = useState(tab === 'changes');
  const [showConfirmationDialog, setShowConfirmationDialog] = useState(false);
  const [approvalDecisions, setApprovalDecisions] = useState(null);
  const [actionsInProgress, setActionsInProgress] = useState({ approve: false, reject: false, delete: false });
  const user = useContext(UserContext);
  const { approvalStore } = useContext(MobxContext);


  useEffect(() => {
    Promise.all([windsorApi.getApprovalRequest(approvalId), windsorApi.getCspGroups()]).then(async ([approval, groups]) => {
      const groupsMap = groups.reduce((accumulator, group) => {
        accumulator[group.ID] = group.Name;
        return accumulator;
      }, {});

      const execution = await windsorApi.getExecution(approval.FamilyGUID, approval.ExecutionGUID);

      setGroups(groupsMap);
      setApprovalDecisions(new ApprovalDetailsHelper(approval, groupsMap, user).getApprovalDecisions(execution.WorkflowCache.DeploymentRecipe));
      setExecution(execution);
      setApproval(approval);
    }).catch(() => {
      setApprovalNotFound(true);
    });

  }, [approvalId, user]);

  useEffect(() => {
    if (approval && execution) {
      diffBuilder.build(approval, execution).then(diffs => {
        setDiffs(diffs);
      });
    }
  }, [approval, execution]);

  function refreshApproval() {
    return windsorApi.getApprovalRequest(approval.GUID).then(approval => {
      setApproval(approval);
    });
  }

  function onTabChange(event: React.ChangeEvent<{}>, newValue: string) {
    if (newValue === 'changes') {
      setLoadChanges(true);
    }
    history.push({ search: `?tab=${newValue}` });
  }

  function onApprove() {
    setActionsInProgress({ ...actionsInProgress, approve: true });
    approvalStore.approve(approval).then(() => {
      refreshApproval();
      enqueueSnackbar('Execution request has been approved', { variant: 'success' });
      setActionsInProgress({ ...actionsInProgress, approve: false });
      // Go back to approvals page
      history.push('/approvals');
    });
  }

  function onReject() {
    setActionsInProgress({ ...actionsInProgress, reject: true });
    approvalStore.reject(approval).then(() => {
      refreshApproval();
      enqueueSnackbar('Execution request has been rejected');
      setActionsInProgress({ ...actionsInProgress, reject: false });
      // Go back to approvals page
      history.push('/approvals');
    });;
  }

  function onDelete() {
    setShowConfirmationDialog(false);
    setActionsInProgress({ ...actionsInProgress, delete: true });
    approvalStore.delete(approval).then(() => {
      enqueueSnackbar('Execution request has been deleted');
      setActionsInProgress({ ...actionsInProgress, delete: false });
      // Go back to approvals page
      history.push('/approvals');
    });;
  }

  function onOpenConfirmationDialog() {
    setShowConfirmationDialog(true);
  }

  function onConfirmationDialogClosed() {
    setShowConfirmationDialog(false);
  }

  function onAddComment(comment: string) {
    return windsorApi.submitApprovalRequestComment(approval.GUID, comment).then(() => refreshApproval());
  }

  if (approvalNotFound) {
    return <NoResultsText variant="h6" color="textSecondary">Approval Not Found</NoResultsText>
  } else if (approval && execution && groups && approvalDecisions) {
    return (
      <div className={classes.root}>
        <div>
          <Breadcrumbs aria-label="breadcrumb">
            <LinkRouter color="textSecondary" to="/approvals">
              Approvals
            </LinkRouter>
            <Typography color="textPrimary">{approval.WorkflowName}</Typography>
          </Breadcrumbs>
          <Divider className={classes.divider} />
          <div className={classes.titleSection}>
            <div>
              <Typography variant="h6" gutterBottom>Approval Request for {approval.WorkflowName} ({approval.FamilyName})</Typography>
              <div>
                <TimestampTooltip timestamp={approval.ApprovalRequestCreationTime * 1000}>
                  <Typography color="textSecondary" display="inline" >
                    Submitted {moment(approval.ApprovalRequestCreationTime * 1000).fromNow()}
                  </Typography>
                </TimestampTooltip>
                <Typography color="textSecondary" display="inline" className={classes.middleDot} >
                  &bull;
              </Typography>
                <Typography color="textSecondary" display="inline" >
                  Environment: {execution.WorkflowCache.AccountName}
                </Typography>
              </div>
            </div>
            <div>
              <LoadingButton
                tooltip="Delete approval request"
                loading={actionsInProgress.delete}
                variant="outlined"
                size="small"
                onClick={onOpenConfirmationDialog}>
                Delete
              </LoadingButton>
            </div>
          </div>

          <div className={classes.infoPanel}>
            <div className={classes.approvalDescriptionText}>
              <Typography variant="body1" display="inline">
                {ExecutionConstants.ExecutionPlanLabels[approval.ExecutionPlan]} for
              </Typography>
              <LinkRouter variant="body1" className={classes.executionLink} to={`/families/${approval.FamilyGUID}/executions/${approval.ExecutionGUID}`}>
                execution
              </LinkRouter>
              <Typography variant="body1" display="inline">
                requested by {approval.Requester}
              </Typography>
            </div>
            <div>
              <LoadingButton
                tooltip="Approve this request"
                loading={actionsInProgress.approve}
                variant="outlined"
                className={classes.approveButton}
                onClick={onApprove}>
                Approve
            </LoadingButton>
              <LoadingButton
                tooltip="Reject this request"
                loading={actionsInProgress.reject}
                variant="outlined"
                className={classes.rejectButton}
                onClick={onReject}>
                Reject
            </LoadingButton>
            </div>
          </div>

          <Tabs
            className={classes.tabs}
            value={tab}
            onChange={onTabChange}
            aria-label="Approval tabs"
            indicatorColor="primary"
            textColor="primary"
          >
            <Tab label="Activity" value="activity" />
            <Tab label="Actions" value="actions" />
            <Tab label="Approvals" value="approvals" />
            <Tab label="History" value="history" />
            <Tab label="Changes" value="changes" />
          </Tabs>
        </div>

        <TabContent display={tab === 'activity'} className={classes.tabContent}>
          <ActivityPanel approvalRecord={approval} onAddComment={onAddComment} />
        </TabContent>
        <TabContent display={tab === 'actions'} className={classes.tabContent}>
          <ActionsPanel
            accountId={execution.AccountCache.AccountID}
            region={execution.AccountCache.Region}
            approvalRecord={approval}
            deploymentRecipe={execution.WorkflowCache.DeploymentRecipe}
          />
        </TabContent>
        <TabContent display={tab === 'history'} className={classes.tabContent}>
          <HistoryPanel approvalRecord={approval} />
        </TabContent>
        <TabContent display={tab === 'approvals'} className={classes.tabContent}>
          <ApprovalsPanel approvalDecisions={approvalDecisions} />
        </TabContent>
        {loadChanges && <TabContent display={tab === 'changes'} className={classes.tabContent}>
          <DiffView diffs={diffs} executionPlan={approval.ExecutionPlan} approvalDecisions={approvalDecisions} />
        </TabContent>}
        <Dialog
          open={showConfirmationDialog}
          onClose={onConfirmationDialogClosed}
          aria-labelledby="confirmation-dialog-title"
          aria-describedby="confirmation-dialog-description"
        >
          <DialogTitle id="confirmation-dialog-title">{"Are you sure you want to delete this approval request?"}</DialogTitle>
          <DialogContent>
            <DialogContentText id="confirmation-dialog-description">
              The proposed execution for the <strong>{approval.WorkflowName}</strong> workflow will be cancelled.
            </DialogContentText>
          </DialogContent>
          <DialogActions>
            <Button onClick={onConfirmationDialogClosed} color="primary">
              Cancel
          </Button>
            <Button onClick={onDelete} color="primary" autoFocus>
              Delete
          </Button>
          </DialogActions>
        </Dialog>
      </div>
    );
  } else {
    return <LoadingIndicator loading={true} />;
  }
};

export default withRouter(withSnackbar(ApprovalsDetailsPage));
